// External
import { signOut, useSession } from 'next-auth/react';
import { useRouter } from 'next/router';
import { PropsWithChildren, useEffect } from 'react';
import * as Sentry from '@sentry/nextjs';
// Internal
import { Status } from '@globalTypes/index';
import { AppPath, LOGOUT_CHANNEL, PUBLIC_ROUTES } from '@utils/constants';

/**
 * Wrapper component that listens for logout broadcasts and triggers the logout process.
 * @param {PropsWithChildren} props - The component props.
 */
export const LogoutWrapper = ({ children }: PropsWithChildren) => {
  const { status } = useSession(); // Get session status
  const { route } = useRouter();

  useEffect(() => {
    const bc = new BroadcastChannel(LOGOUT_CHANNEL);
    switch (status) {
      case Status.loading:
        break;

      case Status.unauthenticated:
        if (!PUBLIC_ROUTES.includes(window.location.pathname as AppPath)) {
          window.location.href = `${AppPath.LOGIN}?callbackUrl=${encodeURIComponent(window.location.href)}`;
        }
        break;

      case Status.authenticated:
        bc.onmessage = () => {
          // NOTE: This timeout is necessary to avoid issues with logout on slow-response pages like Flee Ratings.
          setTimeout(() => {
            const pathname = new URL(window.location.href).pathname;
            Sentry.setUser(null);
            signOut({ redirect: pathname !== AppPath.LOGIN });
          }, 0); // Defers execution until the call stack is clear
        };
    }

    return () => {
      bc.close();
    };
  }, [route, status]);

  return children;
};
